import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { FaSpinner, FaCheckCircle, FaFileContract, FaDownload, FaExclamationTriangle, FaDesktop } from 'react-icons/fa';
import EULA from './auth/EULA';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isDarkMode } = useTheme();

  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [showEULA, setShowEULA] = useState(false);
  const [acceptedEULA, setAcceptedEULA] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [creditSuccess, setCreditSuccess] = useState(false);
  const [creditsAdded, setCreditsAdded] = useState(null);
  const [paidBalance, setPaidBalance] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Read-only observation of the authoritative balance. This page never grants.
  const refreshBalance = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/credits/account`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const account = await response.json();
        setPaidBalance(account.paid_remaining ?? null);
      }
    } catch (err) {
      console.error('Error refetching credit balance:', err);
    } finally {
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      const userId = searchParams.get('user_id');
      const months = searchParams.get('months');

      if (!sessionId) {
        setError('No payment session found. Please try again.');
        setVerifying(false);
        return;
      }

      if (!token) {
        localStorage.setItem('payment_redirect', window.location.search);
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/stripe-success?session_id=${sessionId}&user_id=${userId}&months=${months}`,
          { method: 'GET' }
        );

        if (response.ok) {
          const data = await response.json();
          setPaymentData(data);

          // Credit mode: the server grants credits on the verified session.
          // Never mint credits in the browser -- refetch the authoritative
          // balance from the account endpoint instead.
          if (data.purchase_kind === 'credits') {
            setCreditSuccess(true);
            setVerifying(false);
            // The grant is the server's to make (this page reached it, but the
            // Stripe webhook is the authority and may get there first). Only
            // report a balance once the server says the grant landed; otherwise
            // show the still-processing state with a manual refresh.
            if (data.grant_status === 'granted' || data.grant_status === 'already_granted') {
              setCreditsAdded(data.credits || 0);
              await refreshBalance();
            }
            return;
          }

          if (data.eula_accepted) {
            // Ensure the license was actually created via /confirm
            // before showing success (handles page reloads between EULA accept and confirm)
            const confirmResp = await fetch(
              `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/confirm`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ payment_intent_id: data.payment_intent_id })
              }
            );
            if (confirmResp.ok || confirmResp.status === 404) {
              setSuccess(true);
            } else {
              setShowEULA(true);
            }
          } else {
            setShowEULA(true);
          }
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Payment verification failed');
        }
      } catch (err) {
        setError('Failed to verify payment. Please contact support.');
        console.error('Payment verification error:', err);
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, token, navigate, refreshBalance]);

  const handleEULAAccept = async () => {
    if (!acceptedEULA) {
      setError('You must accept the EULA to continue');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const eulaResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/accept-eula`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            payment_intent_id: paymentData.payment_intent_id,
            accepted: true
          })
        }
      );

      if (!eulaResponse.ok) {
        const errorData = await eulaResponse.json();
        throw new Error(errorData.error || 'Failed to accept EULA');
      }

      const confirmResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            payment_intent_id: paymentData.payment_intent_id
          })
        }
      );

      if (confirmResponse.ok) {
        setShowEULA(false);
        setSuccess(true);
      } else {
        const errorData = await confirmResponse.json();
        setError(errorData.error || 'Payment confirmation failed');
      }
    } catch (err) {
      setError('Failed to complete payment. Please try again.');
      console.error('EULA/Payment error:', err);
    } finally {
      setProcessing(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-purple to-brand-red">
        <div className="text-center">
          <FaSpinner className="w-16 h-16 text-white animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment...</h2>
          <p className="text-white/80">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (error && !showEULA && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-purple to-brand-red p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8">
          <FaExclamationTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className={`text-2xl font-bold text-center mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Payment Verification Failed
          </h2>
          <p className={`text-center mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {error}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 px-4 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 rounded-lg transition-all duration-200"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (creditSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-purple to-brand-red p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 text-center">
          <FaCheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          {creditsAdded !== null ? (
            <>
              <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {creditsAdded} credits added
              </h2>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {paidBalance !== null
                  ? `Paid balance: ${paidBalance}. Paid credits never expire.`
                  : 'Paid credits never expire.'}
              </p>
            </>
          ) : (
            <>
              <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Payment completed, credits are still processing
              </h2>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Your payment went through. The credits are added by Stripe's
                confirmation, which can take a moment -- you can safely close this
                page.
              </p>
              <button
                onClick={refreshBalance}
                disabled={refreshing}
                className={`w-full mb-3 flex items-center justify-center py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 border ${
                  isDarkMode
                    ? 'text-white border-gray-600 hover:bg-gray-800'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                {refreshing ? (
                  <><FaSpinner className="animate-spin w-4 h-4 mr-2" />Checking...</>
                ) : 'Refresh'}
              </button>
              {paidBalance !== null && (
                <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Paid balance: {paidBalance}
                </p>
              )}
            </>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center justify-center py-3 px-4 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <FaDownload className="w-4 h-4 mr-2" />
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-purple to-brand-red p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 text-center">
          <FaCheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Payment Successful!
          </h2>
          <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Your license has been purchased. Download ZapRF and sign in with the same credentials to activate it automatically.
          </p>

          <div className={`p-4 rounded-lg border mb-6 ${
            isDarkMode
              ? 'bg-blue-900/20 border-blue-500/30'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center justify-center mb-2">
              <FaDesktop className={`w-5 h-5 mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                How to Activate
              </h4>
            </div>
            <ol className={`text-xs text-left space-y-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <li>1. Download and install ZapRF</li>
              <li>2. Launch the app and sign in</li>
              <li>3. Your license activates automatically</li>
            </ol>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full flex items-center justify-center py-3 px-4 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <FaDownload className="w-4 h-4 mr-2" />
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showEULA && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-gradient-to-br from-brand-purple to-brand-red-dark rounded-2xl p-1 max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            <div className={`rounded-2xl flex flex-col h-full ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
              <div className="flex-shrink-0 flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className={`text-lg sm:text-2xl font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <FaFileContract className="mr-2 sm:mr-3 text-base sm:text-xl" />
                  End User License Agreement
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6 min-h-0 custom-scrollbar" style={{maxHeight: 'calc(90vh - 280px)'}}>
                <div className="pr-2">
                  <EULA />
                </div>
              </div>

              <div className="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                {error && (
                  <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-100 px-4 py-3 rounded-lg text-sm mb-4">
                    {error}
                  </div>
                )}

                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="acceptEULA"
                    checked={acceptedEULA}
                    onChange={(e) => setAcceptedEULA(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-brand-red focus:ring-2 focus:ring-brand-red cursor-pointer"
                  />
                  <label htmlFor="acceptEULA" className={`ml-3 text-sm cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    I have read and agree to the <span className="font-semibold">End User License Agreement (EULA)</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => navigate('/dashboard')}
                    disabled={processing}
                    className={`px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isDarkMode
                        ? 'text-white/80 hover:text-white border border-white/30 hover:bg-white/10'
                        : 'text-gray-700 border border-gray-300 hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEULAAccept}
                    disabled={!acceptedEULA || processing}
                    className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 rounded-lg transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {processing ? (
                      <>
                        <FaSpinner className="animate-spin w-4 h-4 mr-2" />
                        Processing...
                      </>
                    ) : (
                      'Accept & Activate License'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentSuccess;

